/* ─────────────────────  FamMatch · types & constants  ─────────────────────
 * Single source of truth for the family-side match-finding UI.
 *
 * Post-04918: the family never sees a helper *table*. The org admin
 * types the helper's name + phone per match on the request row
 * itself. So the only types we expose here are the family-side
 * status vocabulary + the Session shape (the row + its helpers
 * snapshot, used by the closed-history list).
 *
 * `need` is free-form text on the schema (column type was
 * `public.helper_need` enum through migration 010; the 04973
 * migration converted it to text — see 04973 for the migration).
 * The UI no longer dictates vocabulary via a button-group.
 */

export type RequestStatus =
  | "open"
  | "assigned"
  | "confirmed"
  | "completed"
  | "declined"
  | "withdrawn";

export type Session = {
  id: string;
  /** Helper name typed by the org admin on assignment. NULL until
   *  the request is in a non-open status. */
  helperName: string;
  /** Helper phone typed by the org admin. Same NULL semantics. */
  helperPhone: string;
  /** Free-form description. Was an enum label pre-04973; see
   *  migration 04973 for the conversion. */
  need: string;
  /** Human-readable when label. Kept as a free-form string so the
   *  parent can format it without re-deriving from status. */
  when: string;
  status: RequestStatus;
};

/* ─────────────────────  Hong Kong — all 18 districts  ─────────────────────
 *
 * The form offered 4 (屯門 / 元朗 / 荃灣 / 深水埗 + 不限). Operators report
 * that 80%+ of match requests come from families in other districts
 * (港島東 / 西貢 / 北區 etc.) and they end up typing the district by
 * hand in the notes. Shipping the full 18 keeps the form's single
 * field as the source of truth and matches gov.hk's "District
 * Councils (18 districts at present)" wording.
 *
 * Order: roughly HK govt `Hong Kong Island` → `Kowloon` → `New
 * Territories` → outlying. 不限 stays first. The IndexedDB and
 * the localStorage-bound history keys indexed by district name;
 * changing the spelling (e.g. 中文) of an entry is a breaking
 * change for any cached open request — keep stable.
 */
export const DISTRICTS: ReadonlyArray<string> = [
  "不限",
  "中西區",
  "灣仔",
  "東區",
  "南區",
  "油尖旺",
  "深水埗",
  "九龍城",
  "黃大仙",
  "觀塘",
  "葵青",
  "荃灣",
  "屯門",
  "元朗",
  "北區",
  "大埔",
  "沙田",
  "西貢",
  "離島",
];

/* ─────────────────────  Languages — 不限 + common + HK minorities  ───────
 *
 * The form offered 不限 + 粵語 + 普通話 + 英語. Operators report
 * South Asian (南亞) and East Asian minority households that
 * request Hindi / Urdu / Nepali help far more often than the
 * pick list suggested. Adding the HK minority-census top rows so
 * the dropdown is the single field of record.
 *
 * The 大帽 / 標籤 entries are written Cantonese-first then bracketed
 * English. The displayed `備註 (自由填寫)` still serves as the escape
 * hatch for any tongue the list doesn't cover, so this list is not
 * exhaustive.
 *
 * Order matters for cached-open-request keys; keep this list
 * append-only when adding new entries.
 */
export const LANGUAGES: ReadonlyArray<string> = [
  "不限",
  "粵語",
  "普通話",
  "英語",
  "客家話",
  "閩南話 (台灣/福建)",
  "潮州話",
  "上海話",
  "四川話",
  "印尼語 (Bahasa Indonesia)",
  "他加祿語 (Filipino / Tagalog)",
  "印地語 (Hindi)",
  "烏爾都語 (Urdu)",
  "尼泊爾語 (Nepali)",
  "泰語",
  "越南語",
  "韓語",
  "日本語",
  "其他 (請於備註填寫)",
];

/* LITE-plan allowance: 2 match requests per month. */
export const LITE_QUOTA = 2;
